import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetColorIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as MergeIcon } from '@elementary/common-ui/assets/20x20/merge.svg';
import {
  DaysBackPicker,
  FilterOption,
  FilterPopover,
  Flex,
  FlexCenter,
  FlexColumn,
  SearchField,
  GroupedTable,
  DisplayPanel,
} from '@elementary/common-ui/components';
import {
  QuickFilter,
  QuickFilters,
} from '@elementary/common-ui/components/Table';
import FilterChipRow from '@elementary/common-ui/components/Table/TableFilters/FilterChipRow';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  CHAT_INCIDENT_ID,
  INCIDENT_ID,
  INCIDENT_LIST_FILTERS,
  DAYS_BACK,
  MERGE_INCIDENT_IDS_PARAM,
} from '@elementary/common-ui/constants/urlParams';
import {
  agentMergeIncidentsPlan,
  currentTreeData,
} from '@elementary/common-ui/atoms';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import useSnackbar from 'shared/hooks/useSnackbar';
import { useTicketingIntegration } from 'shared/hooks/integrations';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { useEnabledTicketingIntegration } from 'shared/hooks/integrations';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { getTreeNodeAssetIds } from '@elementary/common-ui/hooks/treeData/utils';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useIsTreeViewLoading from 'shared/hooks/useIsTreeViewLoading';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import { INCIDENT_LIST_FILTER_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import IncidentsFloatingActionMenu from 'incidents/components/IncidentsFloatingActionMenu';
import useIncidentOverviewSidePanel from 'incidents/hooks/useIncidentOverviewSidePanel';
import useIncidentMetadata from '../hooks/useIncidentMetadata';
import { getIncidentsFilterOptionsQuery } from '../api';
import type { IncidentListItem, IncidentFilterOptionsGroup } from '../types';
import {
  SEVERITY_DISPLAY,
  STATUS_DISPLAY,
  STATUS_GROUP_VALUES,
} from '../constants';
import { getGroupingStrategyDisplay, parseIncidentSortParam } from '../utils';
import useGroupedIncidents from '../hooks/useGroupedIncidents';
import useIncidentColumns from './useIncidentColumns';

const Wrapper = styled(FlexColumn)`
  gap: 24px;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const BASE_QUICK_FILTERS: QuickFilter[] = [
  {
    name: 'Unassigned',
    filters: [
      {
        property: 'ASSIGNEE',
        operator: 'equals',
        value: [NO_ASSIGNEE_VALUE],
      },
    ],
  },
  {
    name: 'High priority',
    tooltip: 'Unassigned, severity is high or critical',
    filters: [
      {
        property: 'ASSIGNEE',
        operator: 'equals',
        value: [NO_ASSIGNEE_VALUE],
      },
      {
        property: 'SEVERITY',
        operator: 'equals',
        value: ['High', 'Critical'],
      },
    ],
  },
  {
    name: 'Critical Assets',
    tooltip: 'Incidents with a monitor on a critical asset',
    icon: <CriticalAssetColorIcon />,
    filters: [
      {
        property: 'CRITICAL_ASSET',
        operator: 'equals',
        value: ['true'],
      },
    ],
  },
  {
    name: 'Merged',
    tooltip: 'Incidents with multiple monitors',
    icon: <MergeIcon width={16} height={16} />,
    filters: [
      {
        property: 'MONITOR_COUNT',
        operator: 'greaterThan',
        value: ['1'],
      },
    ],
  },
];

const TICKET_QUICK_FILTER: QuickFilter = {
  name: 'Has ticket',
  tooltip: 'Incidents with a linked ticket',
  filters: [
    {
      property: 'TICKET_COUNT',
      operator: 'greaterThan',
      value: ['0'],
    },
  ],
};

const IncidentTable = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();
  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();
  const isTreeViewLoading = useIsTreeViewLoading();
  const treeData = useRecoilValue(currentTreeData);
  const navigate = useNavigateWithEnv();
  const setAgentMergeIncidentsPlan = useSetRecoilState(agentMergeIncidentsPlan);

  const debouncedSearch = useDebounce(search);
  const selectedEnvId = useSelectedEnv();
  const { openSnackbar } = useSnackbar();
  const { data: account } = useAccount();
  const { openPanel } = useAiChatPanel();

  const { enabledIntegrations: ticketingIntegrations } =
    useEnabledTicketingIntegration({ envId: selectedEnvId });
  const hasTicketingIntegration = ticketingIntegrations.length > 0;

  const quickFilters = useMemo(
    () =>
      hasTicketingIntegration
        ? [...BASE_QUICK_FILTERS, TICKET_QUICK_FILTER]
        : BASE_QUICK_FILTERS,
    [hasTicketingIntegration],
  );

  const URL_FILTERS_KEY = INCIDENT_LIST_FILTERS;
  const { urlFilters, clearAllFilters, addFilter, clearFilter, updateFilter } =
    useUrlFilters({ urlKey: URL_FILTERS_KEY });

  const sortVariable = useMemo(
    () => parseIncidentSortParam(searchParams.get(`${URL_FILTERS_KEY}_sort`)),
    [searchParams, URL_FILTERS_KEY],
  );

  const {
    severityOptions,
    statusOptions,
    assigneeOptions,
    canEdit,
    usersData,
    updateStatus,
    updateAssignee,
    updateSeverity,
  } = useIncidentMetadata();

  const daysBackParam = searchParams.get(DAYS_BACK);
  const daysBack = daysBackParam ? parseInt(daysBackParam, 10) : 3;
  const selectedDate = dayjs().subtract(daysBack, 'days');

  const { onOpen: openIncidentOverview } = useIncidentOverviewSidePanel();

  const { selectedAssetIds, selectedTestIds } = useMemo(() => {
    const selectedAssetIds: string[] = [];
    const selectedTestIds: string[] = [];

    if (!urlTreeNode && !urlSavedView) {
      return { selectedAssetIds, selectedTestIds };
    }

    const nodes =
      selectedTreeNode && selectedTreeNode.resourceType !== 'folder'
        ? [selectedTreeNode]
        : selectedTreeFolderNodes ?? [];

    nodes.forEach((node) => {
      if (node.resourceType === 'test') {
        selectedTestIds.push(node.id);
      } else {
        selectedAssetIds.push(...getTreeNodeAssetIds(node));
      }
    });

    return { selectedAssetIds, selectedTestIds };
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const isScopeActive = !!urlTreeNode || !!urlSavedView;
  const hasEmptyScope =
    isScopeActive &&
    selectedAssetIds.length === 0 &&
    selectedTestIds.length === 0;
  const isScopeResolving =
    isScopeActive && (isTreeViewLoading || treeData === undefined);
  const showEmptyScopeState = hasEmptyScope && !isScopeResolving;

  const selectedIdsHash = useMemo(() => {
    const testsHash = hashArray(selectedTestIds);
    const assetsHash = hashArray(selectedAssetIds);
    return `${
      isScopeActive ? 'scoped' : 'unscoped'
    }-${testsHash}-${assetsHash}`;
  }, [selectedTestIds, selectedAssetIds, isScopeActive]);

  const baseFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      daysBack: daysBack,
      assetIds: selectedAssetIds,
      testIds: selectedTestIds,
    }),
    [debouncedSearch, daysBack, selectedAssetIds, selectedTestIds],
  );

  const { data: filterOptionsData } = useGraphqlQuery<{
    incidentsV2FilterOptions: IncidentFilterOptionsGroup[];
  }>({
    queryKey: [
      INCIDENT_LIST_FILTER_OPTIONS,
      selectedEnvId,
      daysBack,
      selectedIdsHash,
    ],
    query: getIncidentsFilterOptionsQuery,
    variables: {
      filters: {
        daysBack,
        assetIds: selectedAssetIds,
        testIds: selectedTestIds,
      },
    },
    enabled: !!selectedEnvId && !hasEmptyScope,
  });

  const filterOpts = filterOptionsData?.incidentsV2FilterOptions;

  const {
    groupBy,
    onGroupByChange,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    allIncidents,
    isFetching,
    isTableLoading,
    isGroupedByStatus,
  } = useGroupedIncidents({
    search: debouncedSearch,
    urlFilters,
    sortVariable,
    baseFilters,
    selectedEnvId,
    selectedIdsHash,
    filterOpts,
    enabled: !hasEmptyScope,
  });

  useEffect(() => {
    if (!isLoaded && !isFetching && !isScopeResolving) {
      setIsLoaded(true);
    }
  }, [isFetching, isLoaded, isScopeResolving]);

  const getFilterValues = useCallback(
    (field: string) =>
      filterOpts
        ?.find((f) => f.field.toLowerCase() === field.toLowerCase())
        ?.options.map((o) => ({ label: o.value, value: o.value })) ?? [],
    [filterOpts],
  );

  const filterOptions: FilterOption[] = useMemo(
    () => [
      {
        property: 'SEVERITY',
        propertyLabel: 'Severity',
        operators: ['equals', 'notEquals'],
        options: getFilterValues('SEVERITY'),
      },
      {
        property: 'STATUS',
        propertyLabel: 'Status',
        operators: ['equals', 'notEquals'],
        options: STATUS_GROUP_VALUES.map((value) => ({ label: value, value })),
      },
      {
        property: 'ASSIGNEE',
        propertyLabel: 'Assignee',
        options: [
          { value: NO_ASSIGNEE_VALUE, label: 'No Assignee' },
          ...getFilterValues('ASSIGNEE'),
        ],
      },
      {
        property: 'OWNER',
        propertyLabel: 'Owner',
        options: getFilterValues('OWNER'),
      },
      {
        property: 'DB',
        propertyLabel: 'Database',
        options: getFilterValues('DB'),
      },
      {
        property: 'SCHEMA',
        propertyLabel: 'Schema',
        options: getFilterValues('SCHEMA'),
      },
      {
        property: 'TABLE',
        propertyLabel: 'Table',
        options: getFilterValues('TABLE'),
      },
      {
        property: 'CRITICAL_ASSET',
        propertyLabel: 'Critical Asset',
        operators: ['equals', 'notEquals'],
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
      {
        property: 'MONITOR_COUNT',
        propertyLabel: 'Monitor Count',
        operators: [
          'greaterThan',
          'greaterThanOrEqual',
          'lessThan',
          'lessThanOrEqual',
          'equals',
        ],
        options: getFilterValues('MONITOR_COUNT'),
        isSingularValue: true,
      },
      {
        property: 'ASSET_NAME',
        propertyLabel: 'Asset Name',
        options: getFilterValues('ASSET_NAME'),
      },
      {
        property: 'MONITOR_TYPE',
        propertyLabel: 'Monitor Type',
        options: getFilterValues('MONITOR_TYPE'),
      },
      {
        property: 'GROUPING_STRATEGY',
        propertyLabel: 'Grouped by',
        operators: ['equals', 'notEquals'],
        options: getFilterValues('GROUPING_STRATEGY').map((option) => ({
          ...option,
          label: getGroupingStrategyDisplay(option.value),
        })),
      },
      {
        property: 'TAG',
        propertyLabel: 'Tag',
        options: getFilterValues('TAG'),
      },
      ...(hasTicketingIntegration
        ? [
            {
              property: 'TICKET_COUNT',
              propertyLabel: 'Tickets',
              operators: [
                'greaterThan',
                'greaterThanOrEqual',
                'lessThan',
                'lessThanOrEqual',
                'equals',
              ] as FilterOption['operators'],
              options: getFilterValues('TICKET_COUNT'),
              isSingularValue: true,
            },
          ]
        : []),
    ],
    [getFilterValues, hasTicketingIntegration],
  );

  const handleSingleIncidentChange = useCallback(
    async (
      id: string,
      val: { assignee?: string; status?: string; severity?: string },
    ) => {
      try {
        if (val.status) {
          await updateStatus({ incidentIds: [id], status: val.status });
        }
        if (val.assignee !== undefined) {
          await updateAssignee({
            incidentIds: [id],
            assignee: val.assignee === NO_ASSIGNEE_VALUE ? null : val.assignee,
          });
        }
        if (val.severity) {
          await updateSeverity({ incidentIds: [id], severity: val.severity });
        }
        openSnackbar({ variant: 'success', message: 'Incident updated' });
      } catch {
        openSnackbar({ variant: 'error', message: 'Incident update failed' });
      }
    },
    [updateStatus, updateAssignee, updateSeverity, openSnackbar],
  );

  const resetSelection = useCallback(() => {
    setIsSelectAll(false);
    setSelectedItems([]);
    setExcludedItems([]);
  }, []);

  const selectableIncidents = useMemo(
    () => allIncidents.filter((incident) => incident.status !== 'RESOLVED'),
    [allIncidents],
  );

  const handleGoToMerge = useCallback(() => {
    if (!canEdit) return;
    const ids = isSelectAll
      ? selectableIncidents
          .filter((inc) => !excludedItems.includes(inc.id))
          .map((i) => i.id)
      : selectedItems;
    if (ids.length < 2) return;
    setAgentMergeIncidentsPlan(null);
    const params = new URLSearchParams();
    params.set(MERGE_INCIDENT_IDS_PARAM, ids.join(','));
    navigate(`/incidents/merge?${params.toString()}`);
  }, [
    canEdit,
    isSelectAll,
    selectableIncidents,
    excludedItems,
    selectedItems,
    navigate,
    setAgentMergeIncidentsPlan,
  ]);

  const selectedCount = useMemo(() => {
    if (isSelectAll) {
      return selectableIncidents.length - excludedItems.length;
    }
    return selectedItems.length;
  }, [
    isSelectAll,
    selectableIncidents.length,
    excludedItems.length,
    selectedItems.length,
  ]);

  const handleBulkUpdate = useCallback(
    async (val: { assignee?: string; status?: string; severity?: string }) => {
      if (!canEdit) return;
      const ids = isSelectAll
        ? selectableIncidents
            .filter((inc) => !excludedItems.includes(inc.id))
            .map((inc) => inc.id)
        : selectedItems;
      if (ids.length === 0) return;
      try {
        if (val.status) {
          const displayStatus =
            STATUS_DISPLAY[val.status.toLowerCase()] ?? val.status;
          await updateStatus({ incidentIds: ids, status: displayStatus });
        }
        if (val.assignee !== undefined) {
          await updateAssignee({
            incidentIds: ids,
            assignee: val.assignee === NO_ASSIGNEE_VALUE ? null : val.assignee,
          });
        }
        if (val.severity) {
          const displaySeverity =
            SEVERITY_DISPLAY[val.severity.toLowerCase()] ?? val.severity;
          await updateSeverity({
            incidentIds: ids,
            severity: displaySeverity,
          });
        }
        openSnackbar({ variant: 'success', message: 'Incidents updated' });
      } catch {
        openSnackbar({ variant: 'error', message: 'Incident update failed' });
      }
    },
    [
      canEdit,
      isSelectAll,
      selectableIncidents,
      excludedItems,
      selectedItems,
      updateStatus,
      updateAssignee,
      updateSeverity,
      openSnackbar,
    ],
  );

  const selectedIncidents = useMemo(
    () =>
      isSelectAll
        ? allIncidents.filter(
            (inc) =>
              inc.status !== 'RESOLVED' && !excludedItems.includes(inc.id),
          )
        : selectedItems
            .map((id) => allIncidents.find((inc) => inc.id === id))
            .filter((inc): inc is IncidentListItem => !!inc),
    [isSelectAll, excludedItems, selectedItems, allIncidents],
  );

  const chatContext = useMemo(() => {
    const ids = selectedIncidents.map((inc) => inc.id);
    if (ids.length === 0) return undefined;
    return {
      incidentIds: ids,
      displayLabels: selectedIncidents.map((inc) => inc.title),
    };
  }, [selectedIncidents]);

  useChatContext(chatContext);

  const { isTicketingConnected } = useTicketingIntegration({
    envId: selectedEnvId,
  });

  const columns = useIncidentColumns({
    severityOptions,
    statusOptions,
    assigneeOptions,
    usersData,
    canEdit,
    isAiIncidentsEnabled: account?.ai_features_enabled,
    hasTicketingIntegration: isTicketingConnected,
    onOpenAIChat: (id, startMessage, displayLabel) =>
      openPanel({
        startMessage,
        context: { incidentIds: [id], displayLabels: [displayLabel] },
        origin: 'ai_cta',
      }),
    onChange: handleSingleIncidentChange,
  });

  const highlightedId =
    searchParams.get(INCIDENT_ID) || searchParams.get(CHAT_INCIDENT_ID);

  useViewLoadTracking({
    name: 'Incident List',
    loadDeps: [!isFetching || null],
    contextId: JSON.stringify(urlFilters) + daysBackParam,
  });

  return (
    <Wrapper>
      <FlexColumn sx={{ gap: 2 }}>
        <Flex sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Flex sx={{ gap: 2, flexWrap: 'wrap' }}>
            <SearchField
              sx={{ width: '200px' }}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
            />
            <FilterPopover
              filterOptions={filterOptions}
              onSubmit={(val) => {
                const newValue = (val.value || val.prevValue).filter(
                  (v) => v !== 'all',
                );
                if (val.prevValue?.length || val.prevValue?.includes('all')) {
                  updateFilter(
                    val.property,
                    val.operator,
                    val.prevValue.filter((v) => v !== 'all'),
                    newValue,
                  );
                } else {
                  addFilter(val.property, val.operator, newValue);
                }
              }}
              multiselect
            />
            <QuickFilters
              urlParamsPrefix={URL_FILTERS_KEY}
              quickFilters={quickFilters}
            />
          </Flex>
          <Flex sx={{ alignItems: 'flex-start' }}>
            <Flex sx={{ gap: 1, alignItems: 'center' }}>
              <DisplayPanel
                columns={columns}
                localStorageKey="incident_list_table"
                groupBy={groupBy}
                onGroupByChange={onGroupByChange}
                groupableColumnIds={groupableColumnIds}
                allowUngrouped={false}
              />
              <DaysBackPicker
                value={selectedDate}
                onChange={(val) => {
                  const newDaysBack = dayjs().diff(val, 'days');
                  setSearchParams((prev) => {
                    prev.set(DAYS_BACK, String(newDaysBack));
                    return prev;
                  });
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <FilterChipRow
          filterOptions={filterOptions}
          onSubmit={(val) =>
            updateFilter(
              val.property,
              val.operator,
              val.prevValue!,
              val.value,
              val.index,
            )
          }
          selectedFilters={urlFilters}
          clearFilter={clearFilter}
          onClearAll={clearAllFilters}
        />
      </FlexColumn>

      {groupFetchers}
      {!isLoaded && !showEmptyScopeState ? (
        <FlexCenter sx={{ height: '100%' }}>
          <CircularProgress />
        </FlexCenter>
      ) : (
        <GroupedTable
          data={groupedData}
          columns={columns}
          idKey="id"
          localStorageKey="incident_list_table"
          urlParamsPrefix={URL_FILTERS_KEY}
          selectionColumn={
            canEdit ? (incident) => incident.status !== 'RESOLVED' : undefined
          }
          selectedItems={selectedItems}
          onSelectItem={setSelectedItems}
          isSelectAll={isSelectAll}
          onSelectAllChange={setIsSelectAll}
          excludedItems={excludedItems}
          onExcludeItem={setExcludedItems}
          onRowClick={(incident) => openIncidentOverview(incident.id)}
          focusedRows={highlightedId ? [highlightedId] : []}
          expandedGroups={expandedGroups}
          onGroupToggle={onGroupToggle}
          onGroupScrollToBottom={onGroupScrollToBottom}
          isLoading={
            (isTableLoading || isScopeResolving) && !showEmptyScopeState
          }
          showEmptyGroups={isGroupedByStatus}
          isControlled
          bottomScrollPadding={100}
        />
      )}

      <IncidentsFloatingActionMenu
        selectedCount={selectedCount}
        severityOptions={severityOptions}
        assigneeOptions={assigneeOptions ?? []}
        statusOptions={statusOptions}
        onClose={resetSelection}
        onChange={(val) => handleBulkUpdate(val)}
        onMerge={canEdit ? handleGoToMerge : undefined}
      />
    </Wrapper>
  );
};

export default IncidentTable;
